import { useState, useEffect, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LeadCard } from "@/components/LeadCard";
import { TerritoryMarketplace } from "@/components/TerritoryMarketplace";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { currency, STATES } from "@/components/leadUtils";
import { Flame, MapPin, Briefcase, TrendingUp, Loader2, ShieldCheck, DollarSign } from "lucide-react";

const StatCard = ({ Icon, label, value, testid }) => (
  <Card data-testid={testid} className="bg-slate-900 border-slate-800/80 rounded-xl p-6 flex items-center gap-4">
    <div className="h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
      <Icon className="h-6 w-6 text-emerald-400" />
    </div>
    <div>
      <div className="text-2xl font-heading font-bold text-white">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">{label}</div>
    </div>
  </Card>
);

export default function AgentDashboard() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "all", intent: "all", role: "all" });

  const load = useCallback(async () => {
    try {
      const [my, st, sb] = await Promise.all([
        api.get("/leads/my"),
        api.get("/agent/stats"),
        api.get("/subscriptions/my"),
      ]);
      setMine(my.data.leads);
      setStats(st.data);
      setSubs(sb.data.subscriptions);
    } catch (e) {
      toast.error("Failed to load your territory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = mine.filter((l) =>
    (filters.state === "all" || l.state === filters.state) &&
    (filters.intent === "all" || l.intent === filters.intent) &&
    (filters.role === "all" || l.role === filters.role));

  return (
    <div className="min-h-screen bg-[#080C14] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome, {user?.name}</h1>
            <p className="text-slate-400 mt-1">Own ZIP codes exclusively — every lead in them is automatically yours</p>
          </div>
          <Badge
            data-testid="agent-billing-status-badge"
            className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-mono text-xs uppercase tracking-widest px-3 py-1.5 gap-1.5 w-fit"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {subs.length} ZIP{subs.length === 1 ? "" : "s"} · ${stats?.monthly_cost ?? 0}/mo
          </Badge>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard Icon={MapPin} label="My ZIPs" value={stats?.zip_count ?? "–"} testid="stat-zips" />
          <StatCard Icon={Briefcase} label="Territory Leads" value={stats?.territory_leads ?? "–"} testid="stat-territory-leads" />
          <StatCard Icon={Flame} label="Hot Leads" value={stats?.hot ?? "–"} testid="stat-hot" />
          <StatCard Icon={TrendingUp} label="Pipeline Value" value={stats ? currency(stats.pipeline_value) : "–"} testid="stat-pipeline" />
        </div>

        <Card className="bg-amber-500/5 border-amber-500/30 rounded-xl p-4 mb-8 flex items-center gap-3 text-sm text-amber-200" data-testid="billing-pending-notice">
          <DollarSign className="h-4 w-4 text-amber-400 shrink-0" />
          Billing isn't active yet — ZIP subscriptions are reserved now and monthly charges will start once online payments are enabled.
        </Card>

        <Tabs defaultValue={subs.length ? "mine" : "zips"} key={loading ? "l" : "r"}>
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="mine" data-testid="tab-my-leads" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-slate-950">
              <Briefcase className="h-4 w-4 mr-1.5" /> My Leads ({mine.length})
            </TabsTrigger>
            <TabsTrigger value="zips" data-testid="tab-zip-marketplace" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-slate-950">
              <MapPin className="h-4 w-4 mr-1.5" /> ZIP Territories
            </TabsTrigger>
          </TabsList>

          <TabsContent value="zips" className="mt-6">
            <TerritoryMarketplace onChange={load} />
          </TabsContent>

          <TabsContent value="mine" className="mt-6">
            <div className="flex flex-wrap gap-3 mb-6">
              <Select value={filters.state} onValueChange={(v) => setFilters((f) => ({ ...f, state: v }))}>
                <SelectTrigger data-testid="territory-select-filter" className="w-[200px] bg-slate-900 border-slate-800">
                  <SelectValue placeholder="Territory" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.intent} onValueChange={(v) => setFilters((f) => ({ ...f, intent: v }))}>
                <SelectTrigger data-testid="intent-select-filter" className="w-[160px] bg-slate-900 border-slate-800">
                  <SelectValue placeholder="Intent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Intent</SelectItem>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.role} onValueChange={(v) => setFilters((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="role-select-filter" className="w-[160px] bg-slate-900 border-slate-800">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="buyer">Buyers</SelectItem>
                  <SelectItem value="seller">Sellers</SelectItem>
                  <SelectItem value="both">Buy & Sell</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
            ) : mine.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800 rounded-xl p-16 text-center" data-testid="my-leads-empty">
                <Briefcase className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">{subs.length ? "No leads in your ZIPs yet — new ones will appear here automatically." : "Subscribe to a ZIP territory to start receiving every lead in it."}</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="my-leads-grid">
                {shown.map((lead) => (
                  <LeadCard key={lead.id} lead={lead} revealed={true} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
